import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  BarChart3,
  ArrowRight,
  ArrowLeft,
  Award,
  BookOpen,
  Tag
} from 'lucide-react';
import { Assessment, AssessmentItem } from '../../types';
import {
  CountObjectsComponent,
  TenFrameComponent,
  NumberLineComponent,
  GridActivityComponent,
  FlashcardSetComponent
} from '../ResourceComponents';
import { useTranslation } from '../../contexts/TranslationContext';
import { LaTeXRenderer } from '../Common/LaTeXRenderer';
import { compileSlamChoiceText } from '../../utils/slamTemplateCompiler';

const TenFrameStimulus: React.FC<{ stimulus?: Record<string, any> }> = ({ stimulus }) => {
  const { t } = useTranslation();
  const frameValue = stimulus?.ten_frame || stimulus?.frame_value || 12;
  const rows = stimulus?.rows || 2;
  const cols = stimulus?.cols || 5;
  const frameType = stimulus?.frame_type || 'ten_frame';
  
  const generateFrame = () => {
    const cells = [];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const index = row * cols + col;
        cells.push({
          row,
          col,
          filled: index < frameValue,
          index
        });
      }
    }
    return cells;
  };

  const cells = generateFrame();

  const getFrameTitle = () => {
    switch (frameType) {
      case 'ten_frame':
        return t('assessmentPanel.tenFrame');
      case 'five_frame':
        return t('assessmentPanel.fiveFrame');
      case 'twenty_frame':
        return t('assessmentPanel.twentyFrame');
      case 'custom_frame':
        return `${rows}×${cols} ${t('assessmentPanel.customFrame')}`;
      default:
        return t('assessmentPanel.customFrame');
    }
  };

  const getFrameDescription = () => {
    switch (frameType) {
      case 'ten_frame':
        return t('assessmentPanel.howManyDotsInTenFrame');
      case 'five_frame':
        return t('assessmentPanel.howManyDotsInFiveFrame');
      case 'twenty_frame':
        return t('assessmentPanel.howManyDotsInTwentyFrame');
      case 'custom_frame':
        return t('assessmentPanel.howManyDotsInCustomFrame').replace('{rows}', rows).replace('{cols}', cols);
      default:
        return t('assessmentPanel.howManyDotsInFrame');
    }
  };

  return (
    <div className="bg-green-50 rounded-lg p-6 mb-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-green-900 mb-2">{getFrameTitle()}</h3>
        <p className="text-sm text-green-700">{getFrameDescription()}</p>
      </div>
      
      <div className="flex justify-center">
        <div 
          className={`grid gap-2 p-4 bg-white rounded-lg border-2 border-green-300`}
          style={{
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`
          }}
        >
          {cells.map((cell) => (
            <div
              key={`${cell.row}-${cell.col}`}
              className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center ${
                cell.filled
                  ? 'bg-green-500 border-green-600'
                  : 'bg-gray-100 border-gray-300'
              }`}
            >
              {cell.filled && (
                <div className="w-6 h-6 bg-white rounded-full"></div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      <div className="text-center mt-3">
        <p className="text-sm text-green-600">
          {frameType === 'custom_frame' 
            ? `${rows}×${cols} ${t('assessmentPanel.frameShowingDots')} ${frameValue}`
            : `${getFrameTitle()} ${t('assessmentPanel.frameShowingDots')} ${frameValue}`
          }
        </p>
      </div>
    </div>
  );
};

const NumberLineStimulus: React.FC<{ stimulus?: Record<string, any> }> = ({ stimulus }) => {
  const { t } = useTranslation();
  const numberLine = stimulus?.number_line || '0-20';
  const markPosition = stimulus?.mark || 15;
  const configStep = stimulus?.step; // Allow manual step configuration
  const markStyle = stimulus?.mark_style || 'circle';
  const colorTheme = stimulus?.color_theme || 'purple';
  const showRangeLabels = stimulus?.show_range_labels !== false;
  const showStepInfo = stimulus?.show_step_info !== false;
  
  const [start, end] = numberLine.split('-').map(Number);
  const range = end - start + 1;
  
  // Color theme configuration
  const getThemeColors = () => {
    switch (colorTheme) {
      case 'blue':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-300',
          title: 'text-blue-900',
          subtitle: 'text-blue-700',
          mark: 'bg-blue-500',
          text: 'text-blue-600'
        };
      case 'green':
        return {
          bg: 'bg-green-50',
          border: 'border-green-300',
          title: 'text-green-900',
          subtitle: 'text-green-700',
          mark: 'bg-green-500',
          text: 'text-green-600'
        };
      case 'orange':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-300',
          title: 'text-orange-900',
          subtitle: 'text-orange-700',
          mark: 'bg-orange-500',
          text: 'text-orange-600'
        };
      case 'red':
        return {
          bg: 'bg-red-50',
          border: 'border-red-300',
          title: 'text-red-900',
          subtitle: 'text-red-700',
          mark: 'bg-red-500',
          text: 'text-red-600'
        };
      default: // purple
        return {
          bg: 'bg-purple-50',
          border: 'border-purple-300',
          title: 'text-purple-900',
          subtitle: 'text-purple-700',
          mark: 'bg-purple-500',
          text: 'text-purple-600'
        };
    }
  };
  
  const colors = getThemeColors();
  
  // Calculate responsive layout with manual step override
  const getLayout = () => {
    // If step is manually configured, use it
    if (configStep && configStep > 0) {
      const isLargeStep = configStep >= 5;
      const isVeryLargeStep = configStep >= 10;
      
      return {
        step: configStep,
        buttonSize: isVeryLargeStep ? 'w-5 h-5 text-xs' : isLargeStep ? 'w-6 h-6 text-xs' : 'w-7 h-7 text-sm',
        showAll: configStep === 1
      };
    }
    
    // Otherwise use automatic responsive calculation
    const isLargeRange = range > 15;
    const isVeryLargeRange = range > 30;
    const isExtremelyLargeRange = range > 50;
    
    if (isExtremelyLargeRange) {
      return {
        step: 10,
        buttonSize: 'w-5 h-5 text-xs',
        showAll: false
      };
    } else if (isVeryLargeRange) {
      return {
        step: 5,
        buttonSize: 'w-6 h-6 text-xs',
        showAll: false
      };
    } else if (isLargeRange) {
      return {
        step: 2,
        buttonSize: 'w-7 h-7 text-sm',
        showAll: false
      };
    } else {
      return {
        step: 1,
        buttonSize: 'w-8 h-8 text-sm',
        showAll: true
      };
    }
  };
  
  const layout = getLayout();
  
  // Generate numbers to display based on layout
  const getDisplayNumbers = () => {
    if (layout.showAll) {
      return Array.from({ length: range }, (_, i) => start + i);
    } else {
      return Array.from({ length: Math.ceil(range / layout.step) }, (_, i) => start + (i * layout.step));
    }
  };
  
  const displayNumbers = getDisplayNumbers();
  const markIndex = displayNumbers.findIndex(num => num === markPosition);

  // Generate mark style component
  const renderMark = () => {
    const baseClasses = `absolute bottom-0 transform translate-y-1`;
    switch (markStyle) {
      case 'arrow':
        return (
          <div className={baseClasses}>
            <div className={`w-0 h-0 border-l-[8px] border-r-[8px] border-t-[12px] border-l-transparent border-r-transparent border-t-${colorTheme}-500`}></div>
          </div>
        );
      case 'star':
        return (
          <div className={baseClasses}>
            <div className={`w-6 h-6 ${colors.mark} rounded-sm border-4 border-white shadow-lg transform rotate-45`}></div>
          </div>
        );
      case 'highlight':
        return (
          <div className="absolute inset-0 bg-yellow-200 bg-opacity-50 rounded"></div>
        );
      default: // circle
        return (
          <div className={baseClasses}>
            <div className={`w-6 h-6 ${colors.mark} rounded-full border-4 border-white shadow-lg`}></div>
          </div>
        );
    }
  };

  return (
    <div className={`${colors.bg} rounded-lg p-6 mb-4`}>
      <div className="text-center mb-4">
        <h3 className={`text-lg font-semibold ${colors.title} mb-2`}>{t('assessmentPanel.numberLine')}</h3>
        <p className={`text-sm ${colors.subtitle}`}>{t('assessmentPanel.whatNumberIsMarked')}</p>
      </div>
      
      <div className="flex justify-center">
        <div className={`relative bg-white rounded-lg border-2 ${colors.border} p-4 overflow-x-auto`}>
          <div className="flex items-center space-x-1 min-w-max">
            {displayNumbers.map((num, index) => (
              <div key={num} className="flex flex-col items-center relative">
                <div className={`${layout.buttonSize} flex items-center justify-center font-medium text-gray-700`}>
                  {num}
                </div>
                <div className="w-1 h-4 bg-gray-300"></div>
                {index === markIndex && renderMark()}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {(showRangeLabels || showStepInfo) && (
        <div className="text-center mt-3">
          {showRangeLabels && (
            <p className={`text-sm ${colors.text}`}>
              {t('assessmentPanel.numberLineFrom')} {start} {t('to')} {end}
            </p>
          )}
          {showStepInfo && !layout.showAll && (
            <p className={`text-xs ${colors.text} mt-1 opacity-75`}>
              ({t('numberLine.showingEvery')} {layout.step === 1 ? t('numberLine.number') : layout.step === 2 ? `2nd ${t('numberLine.numbers')}` : layout.step === 5 ? `5th ${t('numberLine.numbers')}` : layout.step === 10 ? `10th ${t('numberLine.numbers')}` : `${layout.step}th ${t('numberLine.numbers')}`})
              {configStep && <span className="ml-1">(configured)</span>}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const BeforeAfterStimulus: React.FC<{ question: AssessmentItem }> = ({ question }) => {
  const { t } = useTranslation();
  const stimulus = question.stimulus || {};
  const baseNumber = stimulus.base_number || 5;
  const questionType = stimulus.question_type || 'before';
  const secondNumber = stimulus.second_number || baseNumber + 2;
  
  const renderQuestion = () => {
    switch (questionType) {
      case 'before':
        return (
          <div className="text-center mb-6">
            <p className="text-lg text-orange-700 mb-4">{t('assessmentPanel.whatNumberComesBefore')}</p>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-16 h-16 bg-orange-200 border-2 border-orange-400 rounded-lg flex items-center justify-center border-dashed">
                <span className="text-2xl font-bold text-orange-600">?</span>
              </div>
              <div className="text-3xl text-orange-600">→</div>
              <div className="w-16 h-16 bg-orange-100 border-2 border-orange-400 rounded-lg flex items-center justify-center">
                <span className="text-2xl font-bold text-orange-800">{baseNumber}</span>
              </div>
            </div>
          </div>
        );
      
      case 'after':
        return (
          <div className="text-center mb-6">
            <p className="text-lg text-orange-700 mb-4">{t('assessmentPanel.whatNumberComesAfter')}</p>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-16 h-16 bg-orange-100 border-2 border-orange-400 rounded-lg flex items-center justify-center">
                <span className="text-2xl font-bold text-orange-800">{baseNumber}</span>
              </div>
              <div className="text-3xl text-orange-600">→</div>
              <div className="w-16 h-16 bg-orange-200 border-2 border-orange-400 rounded-lg flex items-center justify-center border-dashed">
                <span className="text-2xl font-bold text-orange-600">?</span>
              </div>
            </div>
          </div>
        );
      
      case 'between':
        return (
          <div className="text-center mb-6">
            <p className="text-lg text-orange-700 mb-4">{t('assessmentPanel.whatNumberComesBetween')}</p>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-16 h-16 bg-orange-100 border-2 border-orange-400 rounded-lg flex items-center justify-center">
                <span className="text-2xl font-bold text-orange-800">{baseNumber}</span>
              </div>
              <div className="text-3xl text-orange-600">→</div>
              <div className="w-16 h-16 bg-orange-200 border-2 border-orange-400 rounded-lg flex items-center justify-center border-dashed">
                <span className="text-2xl font-bold text-orange-600">?</span>
              </div>
              <div className="text-3xl text-orange-600">→</div>
              <div className="w-16 h-16 bg-orange-100 border-2 border-orange-400 rounded-lg flex items-center justify-center">
                <span className="text-2xl font-bold text-orange-800">{secondNumber}</span>
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="text-center mb-6">
            <p className="text-lg font-medium text-gray-900">{question.prompt}</p>
          </div>
        );
    }
  };
  
  return (
    <div className="bg-orange-50 rounded-lg p-6 mb-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-orange-900 mb-2">{t('assessmentPanel.numberSequence')}</h3>
        <p className="text-sm text-orange-700">{t('assessmentPanel.chooseCorrectNumber')}</p>
      </div>
      
      <div className="bg-white rounded-lg p-6 border-2 border-orange-300">
        {renderQuestion()}
      </div>
    </div>
  );
};

const ComposeStimulus: React.FC<{ question: AssessmentItem }> = ({ question }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-indigo-50 rounded-lg p-6 mb-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-indigo-900 mb-2">{t('assessmentPanel.numberComposition')}</h3>
        <p className="text-sm text-indigo-700">{t('assessmentPanel.writeNumberThatMatches')}</p>
      </div>
      
      <div className="bg-white rounded-lg p-4 border-2 border-indigo-300">
        <div className="text-center">
          <div className="flex justify-center items-center space-x-4">
            <div className="grid grid-cols-5 gap-2 p-3 bg-indigo-100 rounded-lg">
              {Array.from({ length: 10 }, (_, i) => (
                <div key={i} className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              ))}
            </div>
            <div className="text-2xl font-bold text-indigo-600">+</div>
            <div className="grid grid-cols-5 gap-2 p-3 bg-indigo-100 rounded-lg">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SequenceStimulus: React.FC<{ question: AssessmentItem }> = ({ question }) => {
  const { t } = useTranslation();
  const choices = question.choices || [];
  
  return (
    <div className="bg-teal-50 rounded-lg p-6 mb-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-teal-900 mb-2">{t('assessmentPanel.numberSequence')}</h3>
        <p className="text-sm text-teal-700">{t('assessmentPanel.whatComesNextInSequence')}</p>
      </div>
      
      <div className="bg-white rounded-lg p-4 border-2 border-teal-300">
        <div className="flex justify-center space-x-3 mb-4">
          {choices.slice(0, 3).map((choice, index) => (
            <div
              key={index}
              className="w-16 h-16 bg-teal-100 rounded-lg border-2 border-teal-200 flex items-center justify-center"
            >
              <span className="text-xl font-bold text-teal-800">{choice}</span>
            </div>
          ))}
          <div className="w-16 h-16 bg-gray-200 rounded-lg border-2 border-dashed border-gray-400 flex items-center justify-center">
            <span className="text-xl font-bold text-gray-500">?</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const SlamItemStimulus: React.FC<{ question: AssessmentItem }> = ({ question }) => {
  const { t } = useTranslation();
  const difficultyStars = question.slam_difficulty
    ? Array.from({ length: 5 }, (_, i) => i < question.slam_difficulty! ? '★' : '☆').join('')
    : null;

  const intro = question.slam_introduction ?? '';
  const indic = question.slam_indication ?? '';
  const desc = question.slam_description ?? '';

  return (
    <div className="bg-blue-50 rounded-lg p-6 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <BookOpen className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-semibold text-blue-800">{t('slamPicker.slamExercise')}</span>
        </div>
        <div className="flex items-center space-x-2 text-xs text-blue-600">
          {question.slam_domain && (
            <span className="flex items-center space-x-1 bg-blue-100 px-2 py-0.5 rounded-full">
              <Tag className="w-3 h-3" />
              <span>{question.slam_domain}</span>
            </span>
          )}
          {question.slam_title && (
            <span className="flex items-center space-x-1 bg-blue-100 px-2 py-0.5 rounded-full">
              <BookOpen className="w-3 h-3" />
              <span>{question.slam_title}</span>
            </span>
          )}
          {question.slam_skill && (
            <span className="bg-blue-100 px-2 py-0.5 rounded-full">{question.slam_skill}</span>
          )}
          {question.slam_grade && (
            <span className="bg-blue-100 px-2 py-0.5 rounded-full">{question.slam_grade}</span>
          )}
          {difficultyStars && (
            <span className="text-yellow-500 tracking-widest">{difficultyStars}</span>
          )}
        </div>
      </div>

      {intro && (
        <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          <span className="font-semibold">{t('slamPicker.introduction')} : </span>
          <LaTeXRenderer content={intro} className="inline-block ml-1" />
        </div>
      )}

      {indic && (
        <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
          <span className="font-semibold">{t('slamPicker.indication')} : </span>
          <LaTeXRenderer content={indic} className="inline-block ml-1" />
        </div>
      )}

      {desc && (
        <LaTeXRenderer
          content={desc}
          className="bg-white rounded-lg border border-blue-200 p-4 text-gray-800 leading-relaxed"
        />
      )}
    </div>
  );
};

const PatternStimulus: React.FC<{ question: AssessmentItem }> = ({ question }) => {
  const { t } = useTranslation();
  const choices = question.choices || [];
  
  return (
    <div className="bg-pink-50 rounded-lg p-6 mb-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-pink-900 mb-2">{t('assessmentPanel.patternRecognition')}</h3>
        <p className="text-sm text-pink-700">{t('assessmentPanel.findPatternAndSelect')}</p>
      </div>
      
      <div className="bg-white rounded-lg p-4 border-2 border-pink-300">
        <div className="flex justify-center space-x-3 mb-4">
          {choices.slice(0, 3).map((choice, index) => (
            <div
              key={index}
              className="w-16 h-16 bg-pink-100 rounded-lg border-2 border-pink-200 flex items-center justify-center"
            >
              <span className="text-xl font-bold text-pink-800">{choice}</span>
            </div>
          ))}
          <div className="w-16 h-16 bg-gray-200 rounded-lg border-2 border-dashed border-gray-400 flex items-center justify-center">
            <span className="text-xl font-bold text-gray-500">?</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ComparisonStimulus: React.FC<{ question: AssessmentItem }> = ({ question }) => {
  const { t } = useTranslation();
  // Extract numbers to compare from stimulus or use defaults
  const stimulus = question.stimulus || {};
  const number1 = stimulus.number1 || 7;
  const number2 = stimulus.number2 || 12;
  
  return (
    <div className="bg-cyan-50 rounded-lg p-6 mb-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-cyan-900 mb-2">{t('assessmentPanel.numberComparison')}</h3>
        <p className="text-sm text-cyan-700">{t('assessmentPanel.compareNumbersAndSelect')}</p>
      </div>
      
      <div className="bg-white rounded-lg p-4 border-2 border-cyan-300">
        <div className="flex justify-center items-center space-x-4 mb-4">
          <div className="w-20 h-20 bg-cyan-100 rounded-lg border-2 border-cyan-200 flex items-center justify-center">
            <span className="text-2xl font-bold text-cyan-800">{number1}</span>
          </div>
          <div className="text-2xl font-bold text-cyan-600">?</div>
          <div className="w-20 h-20 bg-cyan-100 rounded-lg border-2 border-cyan-200 flex items-center justify-center">
            <span className="text-2xl font-bold text-cyan-800">{number2}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const MultipleChoiceComponent: React.FC<{
  choices: (number | string)[];
  selectedAnswer: number | string | null;
  onAnswer: (answer: number | string) => void;
}> = ({ choices, selectedAnswer, onAnswer }) => {
  const getChoiceLabel = (index: number) => {
    return String.fromCharCode(65 + index); // A, B, C, D, etc.
  };

  return (
    <div className="space-y-3">
      {choices.map((choice, index) => (
        <button
          key={index}
          onClick={() => onAnswer(choice)}
          className={`w-full p-4 rounded-lg border-2 transition-all ${
            selectedAnswer === choice
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-semibold ${
                selectedAnswer === choice
                  ? 'border-primary-500 bg-primary-500 text-white'
                  : 'border-gray-300 bg-gray-100 text-gray-600'
              }`}>
                {getChoiceLabel(index)}
              </div>
                <span className="text-lg font-medium">
                  <LaTeXRenderer
                    content={
                      typeof choice === 'string'
                        ? compileSlamChoiceText(choice)
                        : String(choice)
                    }
                  />
                </span>
            </div>
            {selectedAnswer === choice && (
              <CheckCircle className="w-5 h-5 text-primary-600" />
            )}
          </div>
        </button>
      ))}
    </div>
  );
};

interface AssessmentPanelProps {
  assessment: Assessment;
  assessmentId: string;
  resources?: Record<string, any>;
  onComplete: (results: AssessmentResults) => void;
  onClose: () => void;
}

interface AssessmentResults {
  assessmentId: string;
  totalQuestions: number;
  correctAnswers: number;
  score: number;
  timeSpent: number;
  answers: Array<{
    questionId: string;
    answer: number | string;
    correct: boolean;
    timeSpent: number;
  }>;
}

const QuestionComponent: React.FC<{
  question: AssessmentItem;
  onAnswer: (answer: number | string, timeSpent: number) => void;
  questionNumber: number;
  totalQuestions: number;
  resources?: Record<string, any>;
}> = ({ question, onAnswer, questionNumber, totalQuestions, resources = {} }) => {
  const { t } = useTranslation();
  const [selectedAnswer, setSelectedAnswer] = useState<number | string | null>(null);
  const [startTime, setStartTime] = useState(Date.now());

  // Reset answer and start time when question changes
  useEffect(() => {
    setSelectedAnswer(null);
    setStartTime(Date.now());
  }, [question.id]);

  const handleAnswer = (answer: number | string) => {
    setSelectedAnswer(answer);
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    onAnswer(answer, timeSpent);
  };

  const renderQuestionContent = () => {
    // Check for embedded resource component in stimulus
    if (question.stimulus?.resource_component) {
      const resourceComponent = question.stimulus.resource_component;
      
      // Try to find a matching resource in the resources parameter
      let actualResource = null;
      if (resourceComponent.resource_id && resources[resourceComponent.resource_id]) {
        actualResource = resources[resourceComponent.resource_id];
      }
      
      // Create a component for rendering
      const component = {
        type: 'display',
        component_id: actualResource ? resourceComponent.resource_id : `embedded-${question.id}`,
        props: {}
      };
      
      // Use actual resource if available, otherwise create a mock resource
      const resource = actualResource || {
        id: `embedded-resource-${question.id}`,
        type: resourceComponent.type,
        ...resourceComponent
      };

      // Components are already imported at the top of the file

      switch (resourceComponent.type) {
        case 'count_objects_canvas':
          return (
            <div className="mb-4">
              <CountObjectsComponent
                resource={resource}
                component={component}
                onAction={() => {}} // No action needed for display
              />
            </div>
          );
        case 'ten_frame':
          return (
            <div className="mb-4">
              <TenFrameComponent
                resource={resource}
                component={component}
                onAction={() => {}} // No action needed for display
              />
            </div>
          );
        case 'number_line':
          return (
            <div className="mb-4">
              <NumberLineComponent
                resource={resource}
                component={component}
                onAction={() => {}} // No action needed for display
              />
            </div>
          );
        case 'grid_activity':
          return (
            <div className="mb-4">
              <GridActivityComponent
                resource={resource}
                component={component}
                onAction={() => {}} // No action needed for display
              />
            </div>
          );
        case 'flashcard_set':
          return (
            <div className="mb-4">
              <FlashcardSetComponent
                resource={resource}
                component={component}
                onAction={() => {}} // No action needed for display
              />
            </div>
          );
        default:
          return (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600 mb-2">
                  {t('assessmentPanel.resourceType')}: {resourceComponent.type}
                </div>
                <p className="text-sm text-gray-500">{t('assessmentPanel.resourceComponentNotImplemented')}</p>
              </div>
            </div>
          );
      }
    }

    // Handle regular question types
    switch (question.type) {
      case 'count_objects':
        return <CountObjectsComponent resource={{...question.stimulus, type: 'count_objects_canvas', showCount: false}} component={{
          type: 'display',
          component_id: `embedded-${question.id}`,
          props: {}
        }} onAction={() => {}} />;

      case 'ten_frame_read':
        return <TenFrameStimulus stimulus={question.stimulus} />;

      case 'number_line_point':
        return <NumberLineStimulus stimulus={question.stimulus} />;

      case 'before_after':
        return <BeforeAfterStimulus question={question} />;

      case 'compose':
        return <ComposeStimulus question={question} />;

      case 'sequence':
        return <SequenceStimulus question={question} />;

      case 'pattern':
        return <PatternStimulus question={question} />;

      case 'comparison':
        return <ComparisonStimulus question={question} />;

      case 'slam_item':
        // Rendered above via slam_item_id check — nothing extra needed here
        return null;

      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {/* Question Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {t('assessmentPanel.question')} {questionNumber} {t('of')} {totalQuestions}
        </div>
        <div className="text-sm text-gray-500">
          {t('type')}: {question.type.replace('_', ' ')}
        </div>
      </div>

      {/* SLAM item stimulus — shown whenever the item comes from SLAM, regardless of type */}
      {question.slam_item_id && <SlamItemStimulus question={question} />}

      {/* Question Content */}
      {renderQuestionContent()}

      {/* Question Prompt */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <LaTeXRenderer
          content={question.prompt || ''}
          className="text-lg font-semibold text-gray-900 mb-4"
        />

        {/* Answer Options */}
        {question.choices ? (
          <MultipleChoiceComponent
            choices={question.choices}
            selectedAnswer={selectedAnswer}
            onAnswer={handleAnswer}
          />
        ) : (
          <div className="space-y-3">
            <div className="text-center">
              <p className="text-gray-600 mb-4">{t('assessmentPanel.enterYourAnswer')}</p>
              <input
                type="number"
                placeholder={t('assessmentPanel.enterNumber')}
                value={selectedAnswer || ''}
                className="w-32 p-3 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setSelectedAnswer(null);
                  } else {
                    const numValue = parseInt(value);
                    if (!isNaN(numValue)) {
                      setSelectedAnswer(numValue);
                    }
                  }
                }}
                onBlur={() => {
                  if (selectedAnswer !== null) {
                    handleAnswer(selectedAnswer);
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export const AssessmentPanel: React.FC<AssessmentPanelProps> = ({
  assessment,
  assessmentId,
  resources = {},
  onComplete,
  onClose
}) => {
  const { t } = useTranslation();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Array<{
    questionId: string;
    answer: number | string;
    timeSpent: number;
  }>>([]);
  const [isStarted, setIsStarted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [totalTime, setTotalTime] = useState(0);

  // Process assessment items to expand quiz resources
  const processedItems = useMemo(() => {
    const items: AssessmentItem[] = [];
    
    assessment.items.forEach(item => {
      if (item.type === 'quiz_resource' && item.resource_id && resources[item.resource_id]) {
        // Expand quiz resource into individual questions
        const quizResource = resources[item.resource_id];
        const questions = quizResource.questions || [];
        
        questions.forEach((question: any, index: number) => {
          // Determine the question type based on resource_component or default to multiple_choice
          let questionType = 'multiple_choice';
          let stimulus = undefined;
          
          if (question.resource_component) {
            // Map resource component types to question types
            switch (question.resource_component.type) {
              case 'count_objects_canvas':
                questionType = 'count_objects';
                stimulus = {
                  objects: question.resource_component.objects || question.resource_component.max_objects || 8,
                  layout: question.resource_component.layout || 'random_clustered'
                };
                break;
              case 'ten_frame':
                questionType = 'ten_frame_read';
                stimulus = {
                  ten_frame: question.resource_component.frame_value || question.resource_component.ten_frame || 12,
                  frame_value: question.resource_component.frame_value || question.resource_component.ten_frame || 12,
                  rows: question.resource_component.rows || 2,
                  cols: question.resource_component.cols || 5
                };
                break;
              case 'number_line':
                questionType = 'number_line_point';
                stimulus = {
                  number_line: question.resource_component.range || question.resource_component.number_line || '0-20',
                  mark: question.resource_component.mark_position || question.resource_component.mark || 15
                };
                break;
              default:
                // For other resource components, keep the resource_component structure
                stimulus = { 
                  resource_component: {
                    ...question.resource_component,
                    resource_id: item.resource_id // Link to the parent quiz resource
                  }
                };
                break;
            }
          }
          
          items.push({
            id: `${item.id}_q${index}`,
            type: questionType,
            prompt: question.prompt || `Question ${index + 1}`,
            answer: question.answer,
            choices: question.choices || [],
            stimulus: stimulus
          });
        });
      } else {
        // Regular assessment item
        items.push(item);
      }
    });
    
    return items;
  }, [assessment.items, resources]);

  const currentQuestion = processedItems[currentQuestionIndex];
  const totalQuestions = processedItems.length;

  useEffect(() => {
    if (isStarted && !isCompleted) {
      const interval = setInterval(() => {
        setTotalTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isStarted, isCompleted]);

  const handleStart = () => {
    setIsStarted(true);
  };

  const handleAnswer = (answer: number | string, timeSpent: number) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = {
      questionId: currentQuestion.id,
      answer,
      timeSpent
    };
    setAnswers(newAnswers);

    // Auto-advance to next question after a short delay
    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        handleComplete(newAnswers);
      }
    }, 1000);
  };

  const handleComplete = (finalAnswers: typeof answers) => {
    setIsCompleted(true);
    const correctAnswers = answers.reduce((sum, answer) => {
        const question = assessment.items.find(q => q.id === answer.questionId);
        return question && answer.answer === question.answer ? sum + (question.points || assessment.scoring?.correct_per_item || 1) : sum;
      }, 0);

    const totalPoints = assessment.items.reduce((sum, item) => sum + (item.points || 0), 0);
  
    const score = (correctAnswers / totalPoints) * 100;

    const results: AssessmentResults = {
      assessmentId,
      totalQuestions,
      correctAnswers,
      score,
      timeSpent: totalTime,
      answers: finalAnswers.map(answer => {
        const question = assessment.items.find(q => q.id === answer.questionId);
        return {
          ...answer,
          correct: question ? answer.answer === question.answer : false
        };
      })
    };
    onComplete(results);
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isStarted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          <div className="p-6">
            <div className="text-center mb-6">
              <BarChart3 className="w-16 h-16 text-primary-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('assessmentPanel.assessment')}</h2>
              <p className="text-gray-600">{t('assessmentPanel.readyToTestKnowledge')}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">{t('assessmentPanel.assessmentDetails')}</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>{t('assessmentPanel.totalQuestions')}</span>
                  <span className="font-medium">{totalQuestions}</span>
                </div>
                {assessment.scoring && (
                  <>
                    <div className="flex justify-between">
                      <span>{t('assessmentPanel.pointsPerQuestion')}</span>
                      <span className="font-medium">{assessment.scoring.correct_per_item}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('assessmentPanel.masteryThreshold')}</span>
                      <span className="font-medium">{assessment.scoring.mastery_threshold}/{assessment.scoring.max}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('assessmentPanel.cancel')}
              </button>
              <button
                onClick={handleStart}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                {t('assessmentPanel.startAssessment')}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  if (isCompleted) {
    const correctAnswers = answers.reduce((sum, answer) => {
      const question = assessment.items.find(q => q.id === answer.questionId);
      return question && answer.answer === question.answer ? sum + (question.points || assessment.scoring?.correct_per_item || 1) : sum;
    }, 0);  

    const totalPoints = assessment.items.reduce((sum, item) => sum + (item.points || 0), 0);

    const score = (correctAnswers / totalPoints) * 100;
    const isMastered = assessment.scoring ? correctAnswers >= assessment.scoring.mastery_threshold : score >= 80;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          <div className="p-6">
            <div className="text-center mb-6">
              {isMastered ? (
                <Award className="w-16 h-16 text-green-600 mx-auto mb-4" />
              ) : (
                <BarChart3 className="w-16 h-16 text-orange-600 mx-auto mb-4" />
              )}
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {isMastered ? t('assessmentPanel.congratulations') : t('assessmentPanel.assessmentComplete')}
              </h2>
              <p className="text-gray-600">
                {isMastered ? t('assessmentPanel.youHaveMastered') : t('assessmentPanel.keepPracticingToImprove')}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary-600">{correctAnswers}/{totalQuestions}</div>
                  <div className="text-sm text-gray-600">{t('assessmentPanel.correctAnswers')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary-600">{Math.round(score)}%</div>
                  <div className="text-sm text-gray-600">{t('assessmentPanel.score')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary-600">{formatTime(totalTime)}</div>
                  <div className="text-sm text-gray-600">{t('assessmentPanel.timeSpent')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary-600">
                    {isMastered ? t('assessmentPanel.mastered') : t('assessmentPanel.inProgress')}
                  </div>
                  <div className="text-sm text-gray-600">{t('assessmentPanel.status')}</div>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                {t('assessmentPanel.close')}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Clock className="w-5 h-5 text-gray-500" />
              <span className="text-lg font-semibold text-gray-900">
                {formatTime(totalTime)}
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                {t('assessmentPanel.question')} {currentQuestionIndex + 1} {t('of')} {totalQuestions}
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Question Content */}
        <div className="p-6">
          <QuestionComponent
            question={currentQuestion}
            onAnswer={handleAnswer}
            questionNumber={currentQuestionIndex + 1}
            totalQuestions={totalQuestions}
            resources={resources}
          />
        </div>

        {/* Navigation */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('assessmentPanel.previous')}</span>
            </button>

            <div className="flex space-x-2">
              {assessment.items.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentQuestionIndex(index)}
                  className={`w-3 h-3 rounded-full ${
                    index === currentQuestionIndex
                      ? 'bg-primary-600'
                      : answers[index]
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              disabled={currentQuestionIndex === totalQuestions - 1}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <span>{t('assessmentPanel.next')}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
